import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Flex,
  FormControl,
  Text,
  FormLabel,
  IconButton,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Popover,
  PopoverArrow,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Td,
  Tr,
  useDisclosure,
  Heading,
} from "@chakra-ui/react";
import { RiNumbersLine } from "react-icons/ri";
import { DeleteIcon } from "../../../../constants/commonIcons";
import { AdminPortalAccountLicenseLine } from "../../../../shared/_core/admin.types";
import { useState } from "react";

type Props = {
  row: AdminPortalAccountLicenseLine;
  onUpdateLicense: (licenseLineID: string, quantity: number) => void;
  onDeleteLicense: (licenseLineID: string) => void;
  isLoading: boolean;
};
export function ServiceAccountLicenseRow({
  row,
  onDeleteLicense,
  onUpdateLicense,
  isLoading,
}: Props) {
  const [newQuantity, setNewQuantity] = useState<number>(row.quantity);

  const updateLicenseState = useDisclosure();

  const handleClickUpdateLicense = () => {
    setNewQuantity(row.quantity);
    updateLicenseState.onOpen();
  };
  const handleConfirmUpdateLicense = () => {
    onUpdateLicense(row.licenseLineID, newQuantity);
    updateLicenseState.onClose();
  };

  const deleteLicenseState = useDisclosure();

  const handleClickDeleteLicense = () => {
    deleteLicenseState.onOpen();
  };
  const handleConfirmDeleteLicense = () => {
    onDeleteLicense(row.licenseLineID);
    deleteLicenseState.onClose();
  };

  return (
    <Tr key={row.licenseLineID}>
      <Td>
        <Box
          fontWeight="bold"
          display="flex"
          flexDirection="column"
          gap="1"
          h="full">
          <Badge colorScheme="gray">{row.licenseLineID}</Badge>
          <Flex gap="1">
            {row.detail.type === "core" ? (
              <Badge>Core</Badge>
            ) : (
              <Badge colorScheme="teal">Addon</Badge>
            )}
            {row.detail.internalName}
          </Flex>
        </Box>
      </Td>
      <Td>{row.quantity}</Td>
      <Td>{row.detail.internalDescription}</Td>
      <Td>{row.detail.displayName}</Td>
      <Td>
        <ButtonGroup size="sm" isAttached variant="outline">
          <Popover
            onClose={updateLicenseState.onClose}
            onOpen={handleClickUpdateLicense}
            isLazy
            isOpen={updateLicenseState.isOpen}>
            <PopoverTrigger>
              <IconButton
                isDisabled={isLoading}
                aria-label="Change quantity"
                icon={<RiNumbersLine />}
              />
            </PopoverTrigger>
            <PopoverContent pt="8" pb="4" px="4">
              <PopoverArrow />
              <PopoverCloseButton />
              <Stack spacing="4">
                <FormControl>
                  <FormLabel htmlFor="quantity">Update quantity for "{row.detail.internalName}"</FormLabel>
                  <NumberInput
                    name="quantity"
                    value={newQuantity}
                    onChange={(e) => setNewQuantity(Number.isNaN(+e) ? 0 : +e)}
                    size="sm">
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
                <ButtonGroup size="sm" display="flex" justifyContent="flex-end">
                  <Button
                    variant="outline"
                    colorScheme="gray"
                    onClick={updateLicenseState.onClose}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConfirmUpdateLicense}
                    isDisabled={newQuantity === row.quantity || isLoading}>
                    Update
                  </Button>
                </ButtonGroup>
              </Stack>
            </PopoverContent>
          </Popover>
          <Popover
            onClose={deleteLicenseState.onClose}
            isOpen={deleteLicenseState.isOpen}
            isLazy
            onOpen={handleClickDeleteLicense}>
            <PopoverTrigger>
              <IconButton
                isDisabled={isLoading}
                colorScheme="red"
                aria-label="Remove"
                icon={<DeleteIcon />}
              />
            </PopoverTrigger>
            <PopoverContent pt="8" pb="4" px="4">
              <PopoverArrow />
              <PopoverCloseButton />
              <Stack spacing="4">
                <Heading size="xs">Are you sure you want to remove the license "{row.detail.internalName}" from this account?</Heading>
                <ButtonGroup size="sm" display="flex" justifyContent="flex-end">
                  <Button
                    variant="outline"
                    colorScheme="gray"
                    onClick={deleteLicenseState.onClose}>
                    Cancel
                  </Button>
                  <Button
                    colorScheme="red"
                    onClick={handleConfirmDeleteLicense}
                    isDisabled={isLoading}>
                    Remove
                  </Button>
                </ButtonGroup>
              </Stack>
            </PopoverContent>
          </Popover>
        </ButtonGroup>
      </Td>
    </Tr>
  );
}
